import { Box } from '@mui/material'
import EditableTable, { ActionPayload } from '../components/EditableTable'
import { Community, useActiveCommunities } from '../utils/community'
import React from 'react'
import updateRestaurantCommunity from '../mutations/updateRestaurantCommunity'
import Txt from './Txt'
import Dialog from './Dialog'
import CreateDialog from './CreateDialog'
import { handleMutationError } from '../utils/errors'
import { formatCents } from '../utils/price'
import { getNumberValue } from '../utils/normalize'
import Card from './Card'

const dict = {
	title: 'Gemeinde',
	minOrderValue: 'Gesamt-Mindestbestellwert',
	deliveryFee: 'Lieferkosten je Teilbestellung',
	nSubOrdersPerOrder: 'Teilbestellungen je Bestellung',
	closingMinutesBeforeDelivery: 'Buchungsschluss (Min. vor Lieferung)',
	activeState: 'Aktiv',
}

const COMMUNITY_EDIT_CONFIG = [
	{
		label: 'Gesamt-Mindestbestellwert',
		property: 'minOrderValueInCents',
		type: 'price' as const,
		allowEmpty: true,
		allowZero: true,
	},
	{
		label: 'Lieferkosten je Teilbestellung',
		property: 'deliveryFeeInCents',
		type: 'price' as const,
		allowEmpty: true,
		allowZero: true,
	},
	{
		label: 'Teilbestellungen je Bestellung',
		property: 'nSubOrdersPerOrder',
		type: 'number' as const,
		allowEmpty: false,
		allowZero: false,
	},
	{
		label: 'Buchungsschluss (Min. vor Lieferung)',
		property: 'closingMinutesBeforeDelivery',
		type: 'number' as const,
		allowEmpty: false,
		allowZero: false,
	},
	{
		label: 'Aktiv',
		property: 'isActive',
		type: 'checkbox' as const,
	},
]

const CommunityHeader = () => (
	<Box
		display="flex"
		flexDirection="row"
		justifyContent="space-between"
		alignItems="flex-start"
		gap="1rem"
	>
		<Txt variant="h4" fontWeight="bold" m="0" color="primary.main">
			Gemeinden
		</Txt>
	</Box>
)

const EditRestaurantCommunities = ({
	communities,
	restaurantId,
}: {
	communities: Community[]
	restaurantId: string
}): JSX.Element => {
	const editCommunityRef = React.useRef<Community | null>(null)
	const availableCommunities = useActiveCommunities()

	const communityOptions = React.useMemo(() => {
		const attachedIds = new Set(communities.map((community) => String(community.id)))
		return (availableCommunities || [])
			.filter((community) => !attachedIds.has(String(community.id)))
			.map((community) => ({
				label: community.title,
				value: community.id,
			}))
			.sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }))
	}, [availableCommunities, communities])

	const createCommunityConfig = React.useMemo(
		() => [
			{
				label: 'Gemeinde',
				property: 'communityId',
				type: 'select' as const,
				options: communityOptions,
				allowEmpty: false,
			},
			{
				label: 'Gesamt-Mindestbestellwert',
				property: 'minOrderValueInCents',
				type: 'price' as const,
				allowEmpty: true,
				allowZero: true,
			},
			{
				label: 'Lieferkosten je Teilbestellung',
				property: 'deliveryFeeInCents',
				type: 'price' as const,
				allowEmpty: true,
				allowZero: true,
			},
			{
				label: 'Teilbestellungen je Bestellung',
				property: 'nSubOrdersPerOrder',
				type: 'number' as const,
				allowEmpty: false,
				allowZero: false,
			},
			{
				label: 'Buchungsschluss (Min. vor Lieferung)',
				property: 'closingMinutesBeforeDelivery',
				type: 'number' as const,
				allowEmpty: false,
				allowZero: false,
			},
			{
				label: 'Aktiv',
				property: 'isActive',
				type: 'checkbox' as const,
			},
		],
		[communityOptions]
	)

	const handleCreateSubmit = React.useCallback(
		async ({ data }: { data: Record<string, unknown> }) => {
			const { communityId } = data || {}
			if (!communityId) {
				handleMutationError('updateRestaurantCommunity', 'Community ID fehlt')
				return false
			}
			const minOrderValue = getNumberValue(data, 'minOrderValueInCents')
			const deliveryFee = getNumberValue(data, 'deliveryFeeInCents')
			const nSubOrdersPerOrder = getNumberValue(data, 'nSubOrdersPerOrder')
			const isActive = Boolean(data?.isActive)
			if (minOrderValue === undefined || deliveryFee === undefined) {
				handleMutationError('updateRestaurantCommunity', 'Ungültiger Wert')
				return false
			}
			const result = await updateRestaurantCommunity({
				type: 'CREATE',
				restaurantId,
				communityId: String(communityId),
				customMinOrderValueInCents: minOrderValue,
				customDeliveryFeeDefaultInCents: deliveryFee,
				nSubOrdersPerOrder,
				closingMinutesBeforeDelivery: getNumberValue(data, 'closingMinutesBeforeDelivery'),
			})
			if (!result.success) {
				handleMutationError('updateRestaurantCommunity', result.error)
				return false
			}
			if (!isActive) {
				const deactivateResult = await updateRestaurantCommunity({
					type: 'DEACTIVATE',
					restaurantId,
					communityId: String(communityId),
				})
				if (!deactivateResult.success) {
					handleMutationError('updateRestaurantCommunity', deactivateResult.error)
					return false
				}
			}
			return true
		},
		[restaurantId]
	)

	const createCommunityDefaultValues = React.useMemo(
		() => ({ isActive: true, nSubOrdersPerOrder: 2, closingMinutesBeforeDelivery: 90 }),
		[]
	)

	const handleOpenCreateDialog = React.useCallback(() => {
		Dialog.render({
			renderContent: ({ dismissPortal }) => (
				<CreateDialog
					title="Neue Gemeinde hinzufügen"
					config={createCommunityConfig}
					onSubmit={handleCreateSubmit}
					defaultValues={createCommunityDefaultValues}
					dismissPortal={dismissPortal}
				/>
			),
		})
	}, [createCommunityConfig, createCommunityDefaultValues, handleCreateSubmit])

	const handleEditSubmit = React.useCallback(
		async ({ data }: { data: Record<string, unknown> }) => {
			const community = editCommunityRef.current
			if (!community) {
				handleMutationError('updateRestaurantCommunity', 'Gemeinde fehlt')
				return false
			}
			const minOrderValue = getNumberValue(data, 'minOrderValueInCents')
			const deliveryFee = getNumberValue(data, 'deliveryFeeInCents')
			const nSubOrdersPerOrder = getNumberValue(data, 'nSubOrdersPerOrder')
			const isActive = Boolean(data?.isActive)
			if (minOrderValue === undefined || deliveryFee === undefined) {
				handleMutationError('updateRestaurantCommunity', 'Ungültiger Wert')
				return false
			}
			const result = await updateRestaurantCommunity({
				type: 'UPDATE',
				restaurantId,
				communityId: String(community.id),
				customMinOrderValueInCents: minOrderValue,
				customDeliveryFeeDefaultInCents: deliveryFee,
				nSubOrdersPerOrder,
				closingMinutesBeforeDelivery: getNumberValue(data, 'closingMinutesBeforeDelivery'),
			})
			if (!result.success) {
				handleMutationError('updateRestaurantCommunity', result.error)
				return false
			}
			const wasActive = Boolean(community.parentRestaurantActive)
			if (isActive !== wasActive) {
				const toggleResult = await updateRestaurantCommunity({
					type: isActive ? 'ACTIVATE' : 'DEACTIVATE',
					restaurantId,
					communityId: String(community.id),
				})
				if (!toggleResult.success) {
					handleMutationError('updateRestaurantCommunity', toggleResult.error)
					return false
				}
			}
			return true
		},
		[restaurantId]
	)

	const handleOpenEditDialog = React.useCallback(
		(obj: Community) => {
			const defaultValues = {
				minOrderValueInCents: obj.parentRestaurantMinOrderValueInCents,
				deliveryFeeInCents: obj.parentRestaurantDeliveryFeeDefaultInCents,
				nSubOrdersPerOrder: obj.parentRestaurantNSubOrdersPerOrder,
				closingMinutesBeforeDelivery: obj.parentRestaurantClosingMinutesBeforeDelivery,
				isActive: obj.parentRestaurantActive !== false,
			}
			editCommunityRef.current = obj
			Dialog.render({
				renderContent: ({ dismissPortal }) => (
					<CreateDialog
						title="Gemeinde bearbeiten"
						config={COMMUNITY_EDIT_CONFIG}
						onSubmit={handleEditSubmit}
						defaultValues={defaultValues}
						dismissPortal={dismissPortal}
					/>
				),
			})
		},
		[handleEditSubmit]
	)

	const handleAction = React.useCallback(
		async (obj?: ActionPayload) => {
			if (!obj?.action) return
			if (obj.action === 'create') {
				handleOpenCreateDialog()
				return
			}
			if (obj.action !== 'edit' && obj.action !== 'delete') return
			const community = communities.find((entry) => String(entry.id) === String(obj.id))
			if (!community) {
				handleMutationError('updateRestaurantCommunity', 'Gemeinde fehlt')
				return
			}
			if (obj.action === 'edit') {
				handleOpenEditDialog(community)
				return
			}
		},
		[communities, handleOpenCreateDialog, handleOpenEditDialog]
	)

	const rows = React.useMemo(
		() =>
			communities.map((community) => ({
				...community,
				__type: 'community',
				title: community.title,
				minOrderValue: formatCents(community.parentRestaurantMinOrderValueInCents),
				deliveryFee: formatCents(community.parentRestaurantDeliveryFeeDefaultInCents),
				nSubOrdersPerOrder: community.parentRestaurantNSubOrdersPerOrder ?? '-',
				closingMinutesBeforeDelivery: community.parentRestaurantClosingMinutesBeforeDelivery ?? '-',
				activeState: community.parentRestaurantActive === false ? 'inaktiv' : 'aktiv',
			})),
		[communities]
	)

	return (
		<>
			<Box maxWidth="100%">
				<Card mb="3rem">
					<CommunityHeader />
					<Box mt="1rem">
						<EditableTable
							objects={rows}
							dict={dict}
							onAction={handleAction}
							emptyRowsFallBackContextType="community"
							disableExpandIcons
							hideDelete
						/>
					</Box>
				</Card>
			</Box>
		</>
	)
}

export default EditRestaurantCommunities
